import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";

const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export default function SalesLedgerStrip() {
  const [data, setData] = useState(null);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/sales").then((res) => setData(res.data)).catch(() => setData(null));
  }, []);

  if (!data || data.summary.count === 0) return null;
  const net = data.summary.net;
  const up = net >= 0;
  const recent = data.sales.slice(0, 6);

  return (
    <section data-testid="binder-sales-strip" className="mb-8 border border-emerald-400/20 bg-emerald-500/5 rounded-2xl p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
          <Receipt className="w-4 h-4 text-emerald-400" /> Sales Ledger
          <span className="text-xs text-zinc-500 font-normal">({data.summary.count} sold · private)</span>
        </h2>
        <div className="flex items-center gap-2">
          <span data-testid="sales-strip-net" className={`inline-flex items-center gap-1 text-xs font-mono-plex font-semibold px-2.5 py-1 rounded-full ${up ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {up ? "+" : "−"}{format(Math.abs(net))} vs market
          </span>
          <Link to="/sales" data-testid="sales-strip-open" className="text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors">
            Open ledger →
          </Link>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        {format(data.summary.total_sold)} total revenue from cards you've sold.
      </p>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {recent.map((sale) => {
          const scan = sale.captured_image_url ? resolveUrl(sale.captured_image_url) : null;
          const match = sale.reference_image_url || null;
          const img = (sale.display_image || (match ? "match" : "scan")) === "scan" ? (scan || match) : (match || scan);
          return (
            <div key={sale.id} data-testid={`sales-strip-item-${sale.id}`} className="w-24 shrink-0">
              {img ? (
                <img src={pcImageUrl(img, "240")} onError={pcImageFallback} alt={sale.name} className="w-24 aspect-[3/4] object-cover rounded-lg border border-white/10 grayscale-[0.3]" />
              ) : (
                <div className="w-24 aspect-[3/4] flex items-center justify-center text-[9px] text-zinc-600 border border-white/10 rounded-lg text-center px-1">{sale.name}</div>
              )}
              <p className="text-[10px] text-zinc-400 mt-1 truncate">{sale.name}</p>
              <p className="text-[10px] font-mono-plex font-semibold text-emerald-300">{format(sale.sold_price)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
